import {
  Component,
  effect,
  inject,
  signal,
  WritableSignal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ButtonGroupComponent,
  IButtonOption,
} from '../../data-input/form-inputs/button-group/button-group.component';
import { ISidebar } from '../../../entities/sidebar/sidebar.entity';
import {
  CONFIGURATION_SETTING_KEYS,
  EConfigurationChannel,
  ELicenseChannelState,
  ILicense,
  LICENSE_KEYS,
} from '@web/dtos';
import { AppConfigService } from '../../../services/app-config.service';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import { ISidebarItem } from '../../../entities/sidebar/item.entity';
import { ConfigurationStore } from '../../../services/api/configuration/configuration-store.service';
import { IMenuTab } from '../../../entities/sidebar/menu-tab.entity';
import { MenuTabComponent } from './menutab/menu-tab.component';
import { LicenseStore } from '../../../services/api/license/license-store.service';

@Component({
  selector: 'aw-sidebar',
  templateUrl: './sidebar.component.html',
  imports: [NgClass, MenuTabComponent, FormsModule, ButtonGroupComponent],
  styles: ``,
})
export class SidebarComponent {
  private readonly configService: AppConfigService = inject(AppConfigService);
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  protected readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  protected readonly licenseStore: LicenseStore = inject(LicenseStore);
  protected readonly stage: string | undefined =
    this.configService.get().general.stage;
  protected isExpanded: boolean = true;
  protected readonly channelOptions: IButtonOption<EConfigurationChannel>[] = [
    {
      label: this.cmsTextProvider.license.web,
      value: EConfigurationChannel.Web,
      iconSrc: 'images/amcon-webshop.svg',
    },
    {
      label: this.cmsTextProvider.license.app,
      value: EConfigurationChannel.App,
      iconSrc: 'images/amcon-app.svg',
    },
  ];
  protected readonly selectedChannel: WritableSignal<EConfigurationChannel> =
    signal(EConfigurationChannel.Web);

  sidebarItems: ISidebar = this.createSideBar();

  constructor() {
    effect(() => {
      const hasWebLicense: boolean = this.licenseStore.isAnyWebLicenseActive();
      const hasAppLicense: boolean = this.licenseStore.isAnyAppLicenseActive();

      if (
        this.selectedChannel() === EConfigurationChannel.Web &&
        !hasWebLicense &&
        hasAppLicense
      ) {
        this.selectedChannel.set(EConfigurationChannel.App);
        return;
      }

      if (
        this.selectedChannel() === EConfigurationChannel.App &&
        !hasAppLicense &&
        hasWebLicense
      ) {
        this.selectedChannel.set(EConfigurationChannel.Web);
        return;
      }

      this.sidebarItems = this.createSideBar();
    });
  }

  toggleSidebar() {
    this.isExpanded = !this.isExpanded;
  }

  selectChannel(channel: EConfigurationChannel): void {
    this.selectedChannel.set(channel);
  }

  isLastItem(index: number): boolean {
    return this.sidebarItems.tabs.length === index + 1;
  }

  showCollapseIcon(): string {
    return !this.isExpanded
      ? 'keyboard_double_arrow_right'
      : 'keyboard_double_arrow_left';
  }

  private createSideBar(): ISidebar {
    return {
      tabs: this.createTabs()
        .map(tab => ({
          ...tab,
          items: tab.items.filter(item => this.canShowItem(item)),
          menus: tab.menus
            .map(menu => ({
              ...menu,
              items: menu.items.filter(item => this.canShowItem(item)),
            }))
            .filter(menu => menu.items.length > 0),
        }))
        .filter(tab => tab.heading === 'BUTTONGROUP' || (tab.items.length > 0 || tab.menus.length > 0)),
    };
  }

  private canShowItem(item: ISidebarItem): boolean {
    const licenseAllowed =
      item.requiredLicenses === undefined ||
      (!this.licenseStore.isLoading() &&
        this.licenseStore.hasAnyActiveLicense(
          item.requiredLicenses,
          item.licenseChannel
        ));
    return licenseAllowed;
  }

  private createTabs(): IMenuTab[] {
    const groups: IMenuTab[] = [
      {
        heading: this.cmsTextProvider.sideBar.monitoring,
        collapsible: true,
        expanded: true,
        menus: [],
        items: [
          {
            icon: 'bar_chart',
            label: this.cmsTextProvider.sideBar.statistic,
            routerLink: '',
            disabled: true,
          },
          {
            icon: 'monitor_heart',
            label: this.cmsTextProvider.shared.incidentMonitor,
            routerLink: 'incident-monitor',
          },
          {
            icon: 'manage_search',
            label: this.cmsTextProvider.sideBar.logs,
            routerLink: '',
            disabled: true,
          },
        ],
      },
      {
        heading: this.cmsTextProvider.sideBar.management,
        collapsible: true,
        expanded: true,
        menus: [],
        items: [
          {
            icon: 'shopping_cart',
            label: this.cmsTextProvider.sideBar.orders,
            routerLink: '/cms/admin/orders',
            requiredLicenses: [
              LICENSE_KEYS.DirectSale,
              LICENSE_KEYS.Subscription,
            ],
          },
          {
            icon: 'groups',
            label: this.cmsTextProvider.sideBar.users,
            routerLink: '/cms/admin/users',
            requiredLicenses: [LICENSE_KEYS.Base],
          },
          {
            icon: 'rule',
            label: this.cmsTextProvider.sideBar.customerPermissions,
            routerLink: '',
            disabled: true,
          },
          {
            icon: 'campaign',
            label: this.cmsTextProvider.sideBar.announcementsMotD,
            routerLink: '',
            disabled: true,
          },
        ],
      },
      {
        heading: this.cmsTextProvider.sideBar.collection,
        collapsible: true,
        expanded: true,
        menus: [],
        items: [
          {
            icon: 'image',
            label: this.cmsTextProvider.shared.allImages,
            routerLink: '/cms/images',
            requiredLicenses: [LICENSE_KEYS.Base],
          },
          {
            icon: 'language',
            label: this.cmsTextProvider.shared.language,
            routerLink: 'languages',
            requiredLicenses: [LICENSE_KEYS.Base],
          },
          {
            icon: 'feedback',
            label: this.cmsTextProvider.sideBar.userFeedback,
            routerLink: 'user-feedback',
            requiredLicenses: [LICENSE_KEYS.Base],
          },
        ],
      },
      ...this.configurationTabs(),
      {
        menus: [],
        expanded: true,
        heading: '',
        items: [
          {
            icon: 'palette',
            label: this.cmsTextProvider.shared.corporateDesign,
            routerLink: '/cms/corporate-design',
            requiredLicenses: [LICENSE_KEYS.Base],
          },
        ],
      },
      ...this.designTabs(),
    ];

    return groups;
  }

  private startPageItem(): ISidebarItem[] {
    if (
      this.configurationStore.getBooleanConfiguration(
        CONFIGURATION_SETTING_KEYS.IsCustomTilePageActive
      )
    ) {
      return [
        {
          icon: 'home',
          label: this.cmsTextProvider.sideBar.startPage,
          routerLink: '/cms/general/start-page',
          requiredLicenses: [LICENSE_KEYS.Base],
          licenseChannel: EConfigurationChannel.Web,
        },
      ];
    } else {
      return [];
    }
  }

  private licenseItems(): ISidebarItem[] {
    return [...this.licenseStore.licenses()]
      .sort(
        (firstLicense: ILicense, secondLicense: ILicense): number =>
          firstLicense.position - secondLicense.position
      )
      .filter(
        (license: ILicense): boolean =>
          license.webState === ELicenseChannelState.Active ||
          license.appState === ELicenseChannelState.Active
      )
      .map(
        (license: ILicense): ISidebarItem => ({
          icon: license.icon,
          label: license.label,
          routerLink: `/cms/configuration/${license.key}`,
          requiredLicenses: [license.key],
        })
      );
  }

  private configurationTabs(): IMenuTab[] {
    const licenseItems: ISidebarItem[] = this.licenseItems();

    if (licenseItems.length === 0) {
      return [];
    }

    return [
      {
        heading: this.cmsTextProvider.sideBar.configuration,
        collapsible: true,
        expanded: true,
        menus: [],
        items: licenseItems,
      },
    ];
  }
  private designTabs(): IMenuTab[] {
    const isAppSelected: boolean =
      this.selectedChannel() === EConfigurationChannel.App;

    if (!isAppSelected && this.licenseStore.isAnyWebLicenseActive()) {
      return [
        {
          menus: [],
          collapsible: true,
          expanded: true,
          heading: "BUTTONGROUP",
          items: [],
        },
        {
          menus: [],
          collapsible: true,
          expanded: true,
          heading: this.cmsTextProvider.sideBar.sortiment,
          items: [
            {
              icon: 'confirmation_number',
              label: this.cmsTextProvider.sideBar.sortiment,
              routerLink: '/cms/sortiment',
              requiredLicenses: [
                LICENSE_KEYS.DirectSale,
                LICENSE_KEYS.Subscription,
              ],
            },
            {
              icon: 'ad_group',
              label: 'Ticket-Kategorien',
              routerLink: '/cms/ticket-categories',
              requiredLicenses: [
                LICENSE_KEYS.DirectSale,
                LICENSE_KEYS.Subscription,
              ],
            },
          ],
        },
        {
          heading: this.cmsTextProvider.sideBar.design,
          collapsible: true,
          expanded: true,
          menus: [],
          items: [
            {
              icon: 'text_fields',
              label: this.cmsTextProvider.sideBar.texts,
              routerLink: '/cms/texts/web',
              requiredLicenses: [LICENSE_KEYS.Base],
              licenseChannel: EConfigurationChannel.Web,
            },
            {
              icon: 'mode_edit_outline',
              label: this.cmsTextProvider.sideBar.shopEditor,
              routerLink: '/cms/corporate-design',
              disabled: true,
            },
            ...this.startPageItem(),
          ],
        },
        {
          heading: this.cmsTextProvider.sideBar.elements,
          collapsible: true,
          expanded: true,
          menus: [],
          items: [
            {
              icon: 'widgets',
              label: this.cmsTextProvider.sideBar.appearance,
              routerLink: '/cms/appearance',
              requiredLicenses: [LICENSE_KEYS.Base],
              licenseChannel: EConfigurationChannel.Web,
            },
            {
              icon: 'vertical_align_top',
              label: this.cmsTextProvider.shared.header,
              routerLink: '/cms/header',
              requiredLicenses: [LICENSE_KEYS.Base],
              licenseChannel: EConfigurationChannel.Web,
            },
            {
              icon: 'crop_landscape',
              label: this.cmsTextProvider.shared.banner,
              routerLink: '/cms/banner',
              requiredLicenses: [LICENSE_KEYS.Base],
              licenseChannel: EConfigurationChannel.Web,
            },
            {
              icon: 'vertical_align_bottom',
              label: this.cmsTextProvider.shared.footer,
              routerLink: '/cms/footer',
              requiredLicenses: [LICENSE_KEYS.Base],
              licenseChannel: EConfigurationChannel.Web,
            },
          ],
        },
      ];
    } else if (isAppSelected && this.licenseStore.isAnyAppLicenseActive()) {
      return [
        {
          menus: [],
          collapsible: true,
          expanded: true,
          heading: 'BUTTONGROUP',
          items: [],
        },
        {
          heading: this.cmsTextProvider.sideBar.design,
          collapsible: true,
          expanded: true,
          menus: [],
          items: [
            {
              icon: 'text_fields',
              label: this.cmsTextProvider.sideBar.texts,
              routerLink: '/cms/texts/app',
              requiredLicenses: [LICENSE_KEYS.Base],
              licenseChannel: EConfigurationChannel.App,
            },
          ],
        },
      ];
    }

    return [];
  }
}
